// playerPool.js
import { addBreadcrumb } from '@sentry/browser';
import genericPool from 'generic-pool';
import { player as playerDebug } from './debug.js';
import { Severity } from './sentry.js';
import { create, destroy, getVideoElementPool } from './shakaFactory.js';
import env from '../resource/env.js';
import getVodPlayerPoolSizeMaxKey from '../resource/getVodPlayerPoolSizeMaxKey.js';

const playerPoolDebug = playerDebug.extend('log:playerPool');
const playerLog = ({ messages, data }) => {
  addBreadcrumb({
    category: 'player',
    level: Severity.Info,
    message: messages.join(' '),
    data,
  });
  playerPoolDebug(...messages, data);
};

const getDebugNo = () => {
  let num = Math.floor(Math.random() * 1000);
  num = num.toString();
  while (num.length < 3) num = '0' + num; // pad
  return num;
};

const factory = {
  create: function () {
    const debugNo = getDebugNo();
    playerLog({ messages: [`(debugNo ${debugNo}) create`] });
    return create({ debugNo });
  },
  destroy: function (player) {
    const debugNo = getDebugNo();
    playerLog({ messages: [`(debugNo ${debugNo}) destroy`] });
    return destroy({ debugNo, player });
  },
};

const DEFAULT_PLAYER_POOL_SIZE_MAX = 1;

const opts = {
  // Maximum size of the pool. Must match the Shorts mount window, which
  // getPreloadOffset derives from this same value; anything smaller leaves a
  // mounted short unable to acquire a player, so it never preloads.
  // Read through `env`, not `process.env`: webpack only inlines a statically
  // written `process.env.KEY`, so a runtime-picked key reads the browser shim's
  // empty object and every device lands on the default.
  max:
    Number(env[getVodPlayerPoolSizeMaxKey()]) || DEFAULT_PLAYER_POOL_SIZE_MAX,

  // Minimum size of the pool
  // We set it as 0 intentionally:
  // 1. Lazily initialize the player.
  // 2. Manually initialize it to ensure all the data (required by players) are ready.
  min: 0,
};

const playerPool = genericPool.createPool(factory, opts);

if (typeof window !== 'undefined') {
  // For debugging
  window.playerPool = playerPool;
}

/**
 * When `acquirePlayer` is called,
 * genericPool would call the corresponding `create` automatically.
 * We also keep track of pool related information here.
 * @return {Promise} promise resolved with a shaka player instance
 */
Object.defineProperty(playerPool, 'acquirePlayer', {
  writable: false,
  value: function () {
    playerLog({
      messages: ['acquirePlayer', 'current pool state b4 acquiring'],
      data: {
        pool: {
          available: playerPool.available,
          borrowed: playerPool.borrowed,
          max: playerPool.max,
          min: playerPool.min,
          pending: playerPool.pending,
          size: playerPool.size,
          spareResourceCapacity: playerPool.spareResourceCapacity,
        },
      },
    });
    return this.acquire();
  },
});

/**
 * When `releasePlayer` is called,
 * genericPool would call the corresponding `destroy` automatically.
 * We also keep track of pool related information here.
 * @param {object} player - a specific shaka player instance needed to be release
 * @return {Promise} promise
 */
Object.defineProperty(playerPool, 'releasePlayer', {
  writable: false,
  value: function (player) {
    playerLog({
      messages: ['releasePlayer', 'current pool state b4 releasing'],
      data: {
        player: {
          serialNumber: player.serialNumber,
        },
        pool: {
          available: playerPool.available,
          borrowed: playerPool.borrowed,
          max: playerPool.max,
          min: playerPool.min,
          pending: playerPool.pending,
          size: playerPool.size,
          spareResourceCapacity: playerPool.spareResourceCapacity,
        },
      },
    });
    return this.release(player);
  },
});

/**
 * It aims to provide the information of the instances managed by playerPool
 * @return {object} a status object for the playerPool
 */
Object.defineProperty(playerPool, 'getStatus', {
  writable: false,
  value: function () {
    return {
      available: playerPool.available,
      borrowed: playerPool.borrowed,
      pending: playerPool.pending,
      spareResourceCapacity: playerPool.spareResourceCapacity,
    };
  },
});

/**
 * @param {object} option
 * @param {boolean} option.autoplay - if the video wants to automatically start playing
 * @param {boolean} option.loop - if the video needs to start over when it's finished
 * @param {boolean} option.muted - the audio output should be muted or not
 * @return {void}
 */
Object.defineProperty(playerPool, 'updateVideoElementPool', {
  writable: false,
  value: function (option) {
    const videoElementPool = getVideoElementPool();
    videoElementPool.forEach(videoElement => {
      for (const [key, value] of Object.entries(option)) {
        videoElement[key] = value;
      }
    });
  },
});

/**
 * Resize the live pool so it keeps matching the Shorts mount window when
 * remote config changes it.
 * generic-pool exposes `max` as a read-only getter, but `_config.max` is the
 * value its dispenser actually reads, so writing that is the only way to
 * resize a pool that already exists.
 * Nothing watches `_config.max`, so growing also has to poke the dispenser:
 * without it an acquire already queued keeps waiting until the next
 * acquire/release happens to run one. Shrinking only caps what gets created
 * from here on — no evictor is scheduled, so instances that already exist stay
 * in the pool and keep being handed out.
 * @param {object} option
 * @param {number} option.size - the maximum number of player instances
 * @return {void}
 * @example
 * setShakaPlayerPoolSizeMax({ size: 3 });
 */
const setShakaPlayerPoolSizeMax = ({ size }) => {
  // `Number.isFinite` rather than a `typeof` check: `NaN` is a number and
  // `NaN <= 0` is false, so it would otherwise be written straight into `max`.
  if (!Number.isFinite(size) || size <= 0) return;
  // `_config` is generic-pool internal, so a version bump can take it away.
  // Say so rather than leaving the pool silently stuck at its old size.
  if (!playerPool._config) {
    playerLog({
      messages: [
        'setShakaPlayerPoolSizeMax',
        'skipped: generic-pool no longer exposes _config',
      ],
      data: { size },
    });
    return;
  }
  playerPool._config.max = size;
  // `_dispense` is internal too, so the same version-bump caveat applies; a
  // grow that silently waits for the next acquire is the worst case.
  playerPool._dispense?.();
  playerLog({
    messages: ['setShakaPlayerPoolSizeMax'],
    data: { size },
  });
};

export { setShakaPlayerPoolSizeMax };
export default playerPool;
